import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import IconMap from "../lib/icons/IconMap";
import logo from "../assets/logo/childsplay-logo.png";
import { navLinks } from "../constants";
import { PORTAL_LOGIN_URL } from "../constants/portal";

export const NewHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState<string | null>(null);
  const [activeLink, setActiveLink] = useState("#home");

  const topNavLinks = ["About", "Our Story", "Contact", "Buy Now"];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll-based active link detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Account for header offset

      // Build sections list with current positions on each scroll
      const sections: { id: string; offsetTop: number }[] = [];

      navLinks.forEach(link => {
        const id = link.href.replace("#", "");
        const element = document.getElementById(id);
        if (element) {
          sections.push({ id: link.href, offsetTop: element.offsetTop });
        }
      });

      // Sort sections by their position on the page
      sections.sort((a, b) => a.offsetTop - b.offsetTop);

      // Find the section that's currently in view
      let currentSection = "#home";

      for (const section of sections) {
        if (scrollPosition >= section.offsetTop) {
          currentSection = section.id;
        }
      }

      setActiveLink(currentSection);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Call once on mount to set initial state

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      setMobileOpenDropdown(null);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setMobileOpenDropdown(null);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId.replace("#", ""));
    if (element) {
      const headerOffset = 120; // Account for sticky header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
      setActiveLink(sectionId);
    }
    closeMobileMenu();
  };

  const toggleMobileDropdown = (href: string) => {
    setMobileOpenDropdown(mobileOpenDropdown === href ? null : href);
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const socialIcons = [
    { icon: IconMap.faFacebookF, name: "Facebook" },
    { icon: IconMap.faTwitter, name: "Twitter" },
    { icon: IconMap.faYoutube, name: "YouTube" },
    { icon: IconMap.faCss3, name: "CSS" },
  ];


  return (
    <header className="sticky top-0 z-[1000] w-full">
      {/* Top Header Bar with Dark Background */}
      <div className="bg-gradient-secondary py-3 border-b border-dashed border-[#fccf9c]">
        <div className="body-container">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Left - Contact Information */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm">
              {/* Phone */}
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={IconMap.faPhone} className="w-4 h-4 text-purple-600" />
                <span>Phone: <a href="tel:+353868784903" className="hover:underline">+353 86 878 4903</a></span>
              </div>
              
              {/* Address */}
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={IconMap.faMapMarkerAlt} className="w-4 h-4 text-purple-600" />
                <span>Address: <a href="https://www.google.com/maps/search/?api=1&query=Childs+Play+Pre+School+Brocca+Rd+Mucklagh+Co+Offaly" target="_blank" rel="noopener noreferrer" className="hover:underline">Brocca Road, Mucklagh, Tullamore, Co Offaly, R35 W568</a></span>
              </div>
            </div>

            {/* Right - Utility Icons and Social Media */}
            {/* <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                {socialIcons.map((social, index) => (
                  <a
                    key={index}
                    href="#"
                    className="text-amber-400 hover:text-white transition-colors"
                    aria-label={social.name}
                  >
                    <FontAwesomeIcon icon={social.icon} className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className={`body-container transition-all duration-300 ${isScrolled ? 'py-[7px]' : 'py-[15px]'}`}>
          <div className="flex items-center justify-between">
            {/* Left - Logo and Tagline */}
            <div className="flex items-center gap-4">
              <img
                src={logo}
                alt="Childs Play Mucklagh"
                className={`transition-all duration-300 ${isScrolled ? 'w-16 sm:w-20 md:w-22' : 'w-24 sm:w-28 md:w-22'} object-contain`}
              />
              <p className="hidden md:block text-sm lg:text-base text-gray-600 italic font-serif max-w-[280px] leading-tight">
                Two Generations, One Legacy of Passion, Shaping Life Long Memories
              </p>
            </div>

            {/* Middle - Navigation Links */}
            <nav className="hidden xl-custom:flex items-center gap-10">
              {navLinks.map((link) => (
                <div
                  key={link.href}
                  className="relative"
                  onMouseEnter={() => link.sublinks && setOpenDropdown(link.href)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className={`flex items-center gap-1 text-sm font-medium text-gray-900 hover:text-red-600 transition-colors pb-1 ${
                      activeLink === link.href
                        ? "border-b-2 border-red-600 text-red-600"
                        : ""
                    }`}
                  >
                    {link.label}
                    {link.sublinks && (
                      <FontAwesomeIcon
                        icon={IconMap.faChevronDown}
                        className="w-3 h-3"
                      />
                    )}
                  </a>
                  {link.sublinks && (
                    <div
                      className={`absolute top-full left-0 w-48 transition-all duration-200 z-[9999] ${
                        openDropdown === link.href
                          ? "opacity-100 visible"
                          : "opacity-0 invisible"
                      }`}
                      onMouseEnter={() => setOpenDropdown(link.href)}
                      onMouseLeave={() => setOpenDropdown(null)}
                      style={{ marginTop: "8px" }}
                    >
                      <div className="bg-white rounded-lg shadow-lg border border-gray-100 py-2">
                        {link.sublinks.map((sublink) => (
                          <a
                            key={sublink.href}
                            href={sublink.href}
                            onClick={(e) => {
                              e.preventDefault();
                              scrollToSection(sublink.href);
                            }}
                            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors duration-200 block"
                          >
                            {sublink.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right - Utility Icons and Button */}
            <div className="flex items-center gap-4">
              {/* Portal Login — the live parent/staff app, a separate site.
                  Filled so it reads as the primary action next to the
                  outlined "Book a Visit". Shown at every width: parents on
                  phones are the main audience for it. */}
              <a
                href={PORTAL_LOGIN_URL}
                data-testid="header-portal-login"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 border-2 border-red-600 text-white hover:bg-red-700 hover:border-red-700 transition-colors"
              >
                <FontAwesomeIcon icon={IconMap.faUser} className="w-4 h-4" />
                <span className="text-sm font-medium">Portal Login</span>
              </a>

              {/* Book a Visit Button - Desktop */}
              <button 
                onClick={() => scrollToSection("#contact")}
                className="hidden xl-custom:flex items-center gap-2 px-4 py-2 border-2 border-red-600 rounded-full hover:bg-red-600 group transition-colors"
              >
                <span className="text-sm font-medium text-gray-900 group-hover:text-white transition-colors">
                  Book a Visit
                </span>
              </button>

              {/* Hamburger Menu (Mobile) */}
              <button
                onClick={toggleMobileMenu}
                className="xl-custom:hidden text-gray-700 hover:text-gray-900 transition-colors p-2"
                aria-label="Toggle menu"
              >
                <FontAwesomeIcon
                  icon={isMobileMenuOpen ? IconMap.faTimes : IconMap.faBars}
                  className="w-6 h-6"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Slides down from top */}
      <div
        className={`xl-custom:hidden fixed inset-0 top-0 left-0 w-full h-full bg-[#1a1a2e] z-[9999] transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        {/* Mobile Menu Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <img src={logo} alt="Childs Play Mucklagh" className="h-10" />
          <button
            onClick={closeMobileMenu}
            className="text-white hover:text-orange-400 transition-colors p-2"
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={IconMap.faTimes} className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Menu Content */}
        <div className="flex flex-col h-[calc(100vh-80px)] overflow-y-auto">
          {/* Navigation Links */}
          <nav className="flex-1 px-6 py-6">
            {/* Portal Login first: the one link that leaves the site. */}
            <a
              href={PORTAL_LOGIN_URL}
              data-testid="mobile-portal-login"
              className="flex items-center justify-center gap-2 w-full mb-4 px-4 py-3 rounded-full bg-red-600 text-white text-lg font-bold hover:bg-red-700 transition-colors"
            >
              <FontAwesomeIcon icon={IconMap.faUser} className="w-5 h-5" />
              Portal Login
            </a>
            <ul className="space-y-1">
              {navLinks.map((link) => (
                <li key={link.href}>
                  {link.sublinks ? (
                    <>
                      <button
                        onClick={() => toggleMobileDropdown(link.href)}
                        className={`w-full flex items-center justify-between text-lg font-bold py-4 hover:text-orange-400 transition-colors ${
                          activeLink === link.href ? "text-orange-400" : "text-white"
                        }`}
                      >
                        <span>{link.label}</span>
                        <FontAwesomeIcon
                          icon={IconMap.faChevronRight}
                          className={`w-4 h-4 transition-transform ${
                            mobileOpenDropdown === link.href ? "rotate-90" : ""
                          }`}
                        />
                      </button>
                      {mobileOpenDropdown === link.href && (
                        <ul className="ml-4 space-y-2 border-l-2 border-gray-700 pl-4 py-2">
                          {link.sublinks.map((sublink) => (
                            <li key={sublink.href}>
                              <a
                                href={sublink.href}
                                onClick={(e) => {
                                  e.preventDefault();
                                  scrollToSection(sublink.href);
                                }}
                                className="block text-white text-sm py-2 hover:text-orange-400 transition-colors"
                              >
                                {sublink.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(link.href);
                      }}
                      className={`flex items-center justify-between text-lg font-bold py-4 hover:text-orange-400 transition-colors ${
                        activeLink === link.href ? "text-orange-400" : "text-white"
                      }`}
                    >
                      <span>{link.label}</span>
                      <FontAwesomeIcon
                        icon={IconMap.faChevronRight}
                        className="w-4 h-4"
                      />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Social Media Icons */}
          <div className="border-t border-gray-700 px-6 py-6">
            <div className="flex items-center justify-center space-x-6">
              {socialIcons.map((social, index) => (
                <a
                  key={index}
                  href="#"
                  className="text-white hover:text-orange-400 transition-colors"
                  aria-label={social.name}
                >
                  <FontAwesomeIcon icon={social.icon} className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
